'use client';

import { type ReactNode } from 'react';

interface Props<T> {
  /** The rows to render. */
  items: T[];
  isLoading: boolean;
  error: Error | undefined;
  /** Shown when the list is empty (and not loading / errored). */
  emptyLabel: string;
  /** Leading copy for the error state; the raw error message is appended. */
  errorLabel: string;
  keyOf: (item: T) => string;
  /** Renders one item's message row (a bubble, optionally decorated). */
  renderRow: (item: T) => ReactNode;
}

/**
 * Shared message list for the channel sidebar sheets (Bookmarks, Reports) —
 * the loading / empty / error / list chrome around a column of message rows.
 * Each surface supplies its own data source, row renderer, and copy; the list
 * body is identical. Web equivalent of upstream's single
 * `CommunityChannelMessageListView` reused by every channel sub-page.
 */
export function ChannelMessageListView<T>({
  items,
  isLoading,
  error,
  emptyLabel,
  errorLabel,
  keyOf,
  renderRow,
}: Props<T>) {
  if (error) {
    return (
      <p
        role="alert"
        className="bg-danger/10 rounded-md border border-red-500/30 px-3 py-2 text-xs text-red-200"
      >
        {errorLabel} {error.message ?? ''}
      </p>
    );
  }
  if (isLoading && items.length === 0) {
    return <p className="text-content-4 text-xs">Loading…</p>;
  }
  if (items.length === 0) {
    return <p className="text-content-3 text-sm">{emptyLabel}</p>;
  }
  return (
    <ul className="flex flex-col gap-3">
      {items.map(item => (
        <li key={keyOf(item)}>{renderRow(item)}</li>
      ))}
    </ul>
  );
}
